import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { Flex, Icon } from 'ui';
import moment from 'utils/cops-moment';
import { isObjectNullOrEmpty } from '../../../../../utils/helpers';
import { laneConfig, viewConfig } from '../../../config';
import { dayViews, viewIds } from '../../../config/view';
import calculateCurrentTimeIndicator from '../../../engine/time-indicator-engine';
import { getDateToString } from '../../../utils/date-functions';

const HeaderViewWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(
    ${p => p.amountOfDays ? p.amountOfDays : 0},
    ${p => p.widthDay ? p.widthDay : 0}%
  );
  grid-gap: ${p => p.widthDayBreaker}px;
  position: relative;
`;

const HeaderDayWrapper = styled.div`
  border-bottom: 1px solid #90a2b2;
  border-left: 1px solid #90a2b2;
  border-right: 1px solid #90a2b2;
  font-size: 14px;
  max-height: 40px;
  overflow: hidden;
  background-color: ${p => p.isNonWorkingDay ? '#d8d8d8' : '#f7f9fb'};
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
`;

const Date = styled.div`
  margin-left: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: ${p => p.isMonth ? '20px' : '0px'};
`;

const Hours = styled.div`
  display: grid;
  grid-template-columns: repeat(
    ${p => p.amountOfHours ? p.amountOfHours : 0},
    ${p => p.widthHours ? p.widthHours : 0}%
  );
`;

const Hour = styled.div`
  position: relative;
  border-right: 1px solid #90a2b2;
  font-size: 13px;

  span {
    margin-left: 3px;
  }

  &:after {
    content: '';
    display: block;
    width: 1px;
    height: 35%;
    padding: 0;
    margin: 0;
    background-color: ${p => p.isNonWorkingDay ? '#d8d8d8' : '#f7f9fb'};
    position: absolute;
    right: -1px;
    top: 0px;
  }

  &:last-child {
    border: none;

    &:after {
      display: none;
    }
  }
`;

const CurrentTimeIndicator = styled(Flex)`
  position: absolute;
  left: ${p => p.left};
  margin-top: 16px;
  z-index: 1;
  pointer-events: none;
  ${(p) => {
    if (!p.isIndicatorVisible) {
      return css`
        display: none;
      `;
    }
    return css``;
  }}
`;
const StyledArrowIcon = styled(Icon)`
  line-height: 0.2;
  padding-left: 3px;
`;
const StyledLine = styled.div`
  margin-top: 4px;
  height: ${p => p.height}px;
  border-left: 1px dashed ${p => p.theme.colors.theme};
  margin-left: 7px;
  z-index: 1;
`;
const Month = styled.div`
  width: 100%;
  display: block;
  background: #f7f9fb;
  position: absolute;
  padding-left: 5px;
  font-size: 13px;
`;

/**
 *  Header View component
 *
 * @param {ganttDimensions} object - Dimensions specific for gantt
 * @param {viewDimensions} object - Dimensions specific for view
 */
const HeaderView = ({
  ganttDimensions,
  viewDimensions,
  intl,
  dateReference,
  columnInfoRef,
  lanesInfo,
  view,
}) => {
  const [currentTime, setCurrentTime] = useState(moment());
  const [lineHeight, setLineHeight] = useState(0);
  const [isIndicatorVisible, setIsIndicatorVisible] = useState(true);
  const [indicatorOffset, setIndicatorOffset] = useState(laneConfig.widthFirstColumn - 7);
  const { widthDay } = ganttDimensions;
  const {
    id,
    amountOfHours,
    hoursArray,
    amountOfDays,
    widthHours,
    weeks,
    endHour,
    calendarType,
    workingDays,
  } = viewDimensions;

  const isDay = dayViews.includes(view?.id);

  let date = !isDay ? moment()
    .year(dateReference.year)
    .month(dateReference.month)
    .date(dateReference.day)
    .startOf(view?.calendarType) :
    moment()
      .year(dateReference.year)
      .month(dateReference.month)
      .date(dateReference.day);

  useEffect(() => {
    setCurrentTime(moment());
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, viewConfig.indicatorInterval);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (columnInfoRef) {
      setLineHeight(columnInfoRef.current.offsetHeight);
    }
  }, [columnInfoRef, dateReference, lanesInfo]);

  useEffect(() => {
    if (currentTime && !isObjectNullOrEmpty(viewDimensions)) {
      const startDate = getDateToString(dateReference, { calendarType });
      const viewPortStart = moment(startDate, 'YYYYMMDD');
      // eslint-disable-next-line no-nested-ternary
      const amountOfViewPortDays = id === viewIds.twoWorkWeeks ?
        12 :
        id === viewIds.month ?
          31 :
          workingDays.length * weeks.length;
      const viewPortEnd =
        viewPortStart.add(amountOfViewPortDays - 1, 'day')
          .set({ hour: endHour, minute: 0, second: 0 });

      if (
        currentTime.isSameOrAfter(viewPortStart) &&
        currentTime.isSameOrBefore(viewPortEnd) &&
        workingDays.includes(currentTime.day())
      ) {
        setIndicatorOffset(
          calculateCurrentTimeIndicator(
            currentTime,
            dateReference,
            viewDimensions,
            ganttDimensions,
            view,
          ),
        );
        setIsIndicatorVisible(true);
      } else {
        setIsIndicatorVisible(false);
      }
    }
  }, [currentTime, dateReference, viewDimensions]);

  const getDaysNotShown = (viewId) => {
    if (viewId === viewIds.twoWorkWeeks) {
      return 2;
    }
    return 0;
  };

  return (
    <HeaderViewWrapper
      data-test-id='gantt-header'
      widthDay={widthDay}
      widthDayBreaker={view?.parameters?.widthDayBreaker}
      amountOfDays={amountOfDays}
    >
      <CurrentTimeIndicator left={indicatorOffset} isIndicatorVisible={isIndicatorVisible} ffcn>
        <Icon type='clock' light={true} />
        <StyledArrowIcon type='sort-down' />
        <StyledLine height={lineHeight} />
      </CurrentTimeIndicator>
      {view.id === viewIds.month && <Month>{date.format('MMMM YYYY')}</Month>}
      {weeks?.map((week, weeksIndex) => {
        const isMonth = view?.id === viewIds.month;
        const isDayView = view?.id === viewIds.oneDay || view?.id === viewIds.twoDays;

        // When working with workweeks, we need to add 2 days times the amount of weeks.
        // These are the days that are not visible in the gantt but should be considered in
        // the calculations
        if (weeksIndex >= 1) {
          const daysNotShown = getDaysNotShown(view?.id);
          if (daysNotShown) {
            date = date.add(daysNotShown * weeksIndex, 'days');
          }
        }
        // eslint-disable-next-line complexity
        return week?.map((dayNumber, weekIndex) => {
          if (weeksIndex !== 0 || weekIndex !== 0) {
            date = date.add(1, 'days');
          }

          const day = isDayView || isMonth ?
            date.day() :
            dayNumber;
          const isNonWorkingDay = view.parameters?.nonWorkingDays?.includes(day);
          const options =
            amountOfHours <= 2
              ? {
                month: 'numeric',
                day: 'numeric',
              }
              : {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              };
          return (
            <HeaderDayWrapper
              data-test-id='gantt-column-header'
              isNonWorkingDay={isNonWorkingDay}
              key={dayNumber}
            >
              <Date isMonth={isMonth}>
                {!isMonth
                  ? `${intl.formatMessage({
                    // eslint-disable-next-line
                    id: `planningBoard.days.${date.locale('en').format('dddd').toUpperCase()}${amountOfHours <= 2 ? '_SHORT' : ''}`,
                  })} ${intl.formatDate(date, options)}`
                  : intl.formatDate(date, { day: 'numeric' })}
              </Date>
              {!isMonth &&
                <Hours
                  data-test-id='hours-column-header'
                  widthHours={widthHours}
                  amountOfHours={amountOfHours}
                >
                  {hoursArray?.map(hour =>
                    <Hour
                      data-test-id='hour-column-header'
                      isNonWorkingDay={isNonWorkingDay}
                      key={hour}
                    >
                      <span>{hour}</span>
                    </Hour>,
                  )}
                </Hours>
              }
            </HeaderDayWrapper>
          );
        });
      })}
    </HeaderViewWrapper>
  );
};

HeaderView.propTypes = {
  ganttDimensions: PropTypes.object,
  viewDimensions: PropTypes.object,
  intl: PropTypes.object,
  dateReference: PropTypes.object,
  columnInfoRef: PropTypes.object,
  lanesInfo: PropTypes.array,
  view: PropTypes.object,
};

export default injectIntl(HeaderView);
